import { BCButton, findMessage, IButtonClickEventArgs, IButtonProps } from '@bimser/components';
import { FormItemTypes, IDictionary } from '@bimser/core';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import { JSX } from 'react';
import {
	ButtonType,
	IPropertyField,
	MultiLanguageText,
	SizeType
} from '../../../../entities/common';
import { IUserEvent } from '../../../../entities/services';
import VisualControl from '../../base/VisualControl';
import { IClickEventArgs } from '../../entities/EventArgs';
import { IButtonProperties } from './entities/IButtonProperties';

export default class Button extends VisualControl {
	text: MultiLanguageText;
	buttonType: ButtonType;
	icon: string;
	sizeType: SizeType;
	danger: boolean;
	ghost: boolean;
	block: boolean;
	tabIndex: number;

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.Button;

		this.caption.visible = false;
		this.buttonType = 'default';
		this.tabIndex = 0;
		this._hideCommonLoadingOverlay = true;

		this.subscribe('trigger:OnClick', () => {
			this.triggerClick();
		});
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'text'];
	}

	initialize() {
		super.initialize();
		this.text = new MultiLanguageText(
			(text: string, culture: string, existCultures: string[]) =>
				this.__onChangeMLTextValue(text, 'text', culture, existCultures)
		);
		this.text.setData({ [this.__form.currentLanguage.culture]: findMessage.get('100564') });
	}

	__loadProperties(data: IButtonProperties) {
		super.__loadProperties(data);

		if (data.text) {
			this.text.setData(data.text);
		}

		this.__proxyChanged.buttonType = data.buttonType;
		this.__proxyChanged.icon = data.icon;
		this.__proxyChanged.sizeType = data.sizeType;
		this.__proxyChanged.danger = data.danger;
		this.__proxyChanged.ghost = data.ghost;
		this.__proxyChanged.block = data.block;
		this.__proxyChanged.tabIndex = data.tabIndex;
	}

	private triggerClick(args?: IButtonClickEventArgs) {
		const argument = {
			name: this.name
		};

		this.emit<IClickEventArgs>('event:OnClick', argument);
		this.emit<IUserEvent<IClickEventArgs>>('event:server:OnClick', {
			argument,
			controlId: this.name,
			name: 'Click',
			sender: 'web'
		});

		if (args?.event?.stopPropagation) {
			args.event.stopPropagation();
		}
	}

	private triggerDoubleClick(args: IButtonClickEventArgs) {
		const argument = {
			name: this.name
		};

		this.emit<IClickEventArgs>('event:OnDoubleClick', argument);
		this.emit<IUserEvent<IClickEventArgs>>('event:server:OnDoubleClick', {
			argument,
			controlId: this.name,
			name: 'DoubleClick',
			sender: 'web'
		});

		args.event.stopPropagation();
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'buttonType',
				type: 'enum',
				enumMembers: ['default', 'primary', 'dashed', 'text', 'link'] as ButtonType[]
			},
			{
				name: 'icon',
				type: 'string'
			}
		];
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__onControlClick() {
		// Override VisualControl click method
	}

	__onControlDoubleClick() {
		// Override VisualControl dbclick method
	}

	__getComponentData(): IButtonProps & IDictionary<any> {
		return {
			...super.__getComponentData(),
			text: this.text.getText(),
			buttonType: this.buttonType,
			icon: this.icon as any,
			sizeType: this.sizeType,
			danger: this.danger,
			ghost: this.ghost,
			block: this.block,
			tabIndex: this.tabIndex,
			disabled: this.__isDisabled(),
			loading: this.loading ? { delay: 100 } : false
		};
	}

	__render(children?: JSX.Element) {
		const Icon = GetIcon16ByName(this.icon);

		return super.__render(
			<BCButton
				tabIndex={this.tabIndex}
				text={this.text.getText()}
				icon={Icon ? <Icon /> : undefined}
				size={this.__getSizeType()}
				type={this.buttonType}
				onClick={this.triggerClick.bind(this)}
				onDoubleClick={this.triggerDoubleClick.bind(this)}
				disabled={this.__isDisabled()}
				style={this.style}
				loading={this.loading ? { delay: 100 } : false}
				danger={this.danger}
				ghost={this.ghost}
				block={this.block}
			/>
		);
	}
}
